import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Save } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "full_time", label: "Full Time" },
  { key: "school_age", label: "School Age" },
  { key: "part_time", label: "Part Time" },
  { key: "after_school", label: "After School" },
];

const MEALS = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "snack", label: "Snack" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

const emptyGrid = () => {
  const grid = {};
  CATEGORIES.forEach((c) => {
    grid[c.key] = { breakfast: false, lunch: false, snack: false };
  });
  return grid;
};

export const MealSettingsPanel = ({ authHeaders, isOwner }) => {
  const [grid, setGrid] = useState(emptyGrid());
  const [cutoffTime, setCutoffTime] = useState("09:00");
  const [tz, setTz] = useState("America/New_York");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedBy, setLastSavedBy] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/meal-settings`, { headers: authHeaders });
      const settings = res.data.settings;
      if (settings) {
        setGrid(settings.eligibilityGrid || emptyGrid());
        setCutoffTime(settings.cutoffTime || "09:00");
        setTz(settings.timezone || "America/New_York");
        setLastSavedBy(settings.updatedBy ? { by: settings.updatedBy, at: settings.updatedAt } : null);
      }
    } catch (err) {
      console.error("Error fetching meal settings:", err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleCell = (catKey, mealKey) => {
    setGrid((g) => ({ ...g, [catKey]: { ...g[catKey], [mealKey]: !g[catKey][mealKey] } }));
  };

  const formatCutoff12h = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/inventory/meal-settings`, {
        eligibilityGrid: grid, cutoffTime, timezone: tz,
      }, { headers: authHeaders });
      if (res.data.snapshotTakenNow) {
        toast.success("Settings saved — today's count is ready");
      } else if (!res.data.cutoffPassedToday) {
        toast.success(`Settings saved — first count available after ${formatCutoff12h(cutoffTime)}`);
      } else {
        toast.success("Meal settings saved");
      }
      setLastSavedBy({ by: res.data.settings.updatedBy, at: res.data.settings.updatedAt });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save meal settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) {
    return (
      <p data-testid="meal-settings-not-owner" className="text-sm text-slate-500 py-6 text-center">
        Only Owners and Co-Owners can set up automatic meal counts.
      </p>
    );
  }

  if (loading) {
    return <p className="text-sm text-slate-400 py-6 text-center">Loading meal settings...</p>;
  }

  return (
    <div data-testid="meal-settings-panel" className="space-y-6">
      <p className="text-sm text-slate-500">
        One-time setup: tell us which groups of children count toward each meal, and the one time
        of day every child should be signed in by. Every day, we'll automatically snapshot who's
        still here at that moment and feed it into your Take Inventory "Avg for this meal" reference.
      </p>

      <div className="border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="meal-eligibility-grid">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Category</th>
              {MEALS.map((m) => (
                <th key={m.key} className="text-center px-3 py-2">{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((cat) => (
              <tr key={cat.key} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium text-slate-800">{cat.label}</td>
                {MEALS.map((meal) => (
                  <td key={meal.key} className="text-center px-3 py-2">
                    <Checkbox
                      data-testid={`eligibility-${cat.key}-${meal.key}`}
                      checked={!!grid[cat.key]?.[meal.key]}
                      onCheckedChange={() => toggleCell(cat.key, meal.key)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            <Clock className="w-3.5 h-3.5 inline mr-1" />
            Enter your center's cut-off time
          </label>
          <Input type="time" data-testid="meal-cutoff-time-input" value={cutoffTime}
            onChange={(e) => setCutoffTime(e.target.value)} className="w-32" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Timezone</label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger className="w-40" data-testid="meal-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((z) => (
                <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="save-meal-settings-button" disabled={saving} onClick={handleSave}>
          <Save className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>

      {lastSavedBy && (
        <p className="text-xs text-slate-400">
          Last saved by {lastSavedBy.by} on {new Date(lastSavedBy.at).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default MealSettingsPanel;
